import { useState, useEffect } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, User, Clock, MapPin, Phone, DollarSign, AlertCircle, CheckCircle, Grid, List, RefreshCw, X, Navigation, Edit2, Trash2, Check, Filter, UserPlus, Play, FileText, Plus, Package } from 'lucide-react';
import { isRecurringParent, isRecurringInstance, formatRecurrenceRule } from './recurringJobUtils';
import { getLocalDateString } from './dateUtils';
import { useFeatures } from './FeatureContext';
import { useTheme } from './ThemeContext';
import { isOfflineModeEnabled, getCachedData, cacheData } from './offlineCache';
import JobPhotos from './JobPhotos';
import JobCompletionModal from './JobCompletionModal';
import QuoteVisitCompletionModal from './QuoteVisitCompletionModal';

export default function PlanningScheduling({ companyId, onNavigate, createJobFromQuote, prefillCustomer, currentUserId, isMobile }) {
  const { hasFlexWorkers, getFlexDaysIncluded } = useFeatures();
  const { colors } = useTheme();
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTechs, setSelectedTechs] = useState([]); // Multi-select: empty = all
  const [showTechFilter, setShowTechFilter] = useState(false);
  const [draggedJob, setDraggedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null); // For job details modal
  const [editingJob, setEditingJob] = useState(null); // For edit job modal
  const [saving, setSaving] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedJobForCompletion, setSelectedJobForCompletion] = useState(null);
  const [showQuoteVisitModal, setShowQuoteVisitModal] = useState(false);
  const [selectedQuoteVisitJob, setSelectedQuoteVisitJob] = useState(null);

  // Create job from quote modal
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);
  const [quoteToSchedule, setQuoteToSchedule] = useState(null);
  const [newJobDate, setNewJobDate] = useState('');
  const [newJobTime, setNewJobTime] = useState('09:00');
  const [newJobTech, setNewJobTech] = useState('');
  const [creatingJob, setCreatingJob] = useState(false);

  // Real data
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [unscheduledJobs, setUnscheduledJobs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [flexWorkers, setFlexWorkers] = useState([]);
  const [flexDaysUsed, setFlexDaysUsed] = useState(0);

  // Calendar events (meetings, training, etc.)
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    event_date: '',
    start_time: '09:00',
    duration_minutes: 60,
    assigned_to: '',
    event_type: 'meeting',
    description: '',
    color: '#9b59b6'
  });
  const [savingEvent, setSavingEvent] = useState(false);

  // Business hours for collapsible off-hours
  const [businessHoursStart, setBusinessHoursStart] = useState(8); // hour (0-23)
  const [businessHoursEnd, setBusinessHoursEnd] = useState(17);
  const [showEarlyHours, setShowEarlyHours] = useState(false);
  const [showLateHours, setShowLateHours] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadTechnicians();
      loadJobs();
      loadCalendarEvents();
      loadBusinessHours();
      if (hasFlexWorkers()) {
        loadFlexWorkers();
      }
    } else {
      setLoading(false);
    }
  }, [companyId, selectedDate]);

  const loadBusinessHours = async () => {
    try {
      const { data } = await supabaseData
        .from('companies')
        .select('business_hours_start, business_hours_end')
        .eq('id', companyId)
        .single();
      if (data) {
        if (data.business_hours_start) {
          setBusinessHoursStart(parseInt(data.business_hours_start.split(':')[0]));
        }
        if (data.business_hours_end) {
          setBusinessHoursEnd(parseInt(data.business_hours_end.split(':')[0]));
        }
      }
    } catch (e) {
      // Use defaults
    }
  };

  // Handle creating job from quote (passed from WorkOrders)
  useEffect(() => {
    if (createJobFromQuote) {
      setQuoteToSchedule(createJobFromQuote);
      setNewJobDate(getLocalDateString(new Date()));
      setNewJobTime('09:00');
      setShowCreateJobModal(true);
    }
  }, [createJobFromQuote]);

  // Create job from quote
  const handleCreateJobFromQuote = async () => {
    if (!quoteToSchedule || !newJobDate) return;

    setCreatingJob(true);
    try {
      // Generate job number
      const { data: lastJob } = await supabaseData
        .from('jobs')
        .select('job_number')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      let nextNumber = 1;
      if (lastJob?.job_number) {
        const match = lastJob.job_number.match(/(\d+)$/);
        if (match) nextNumber = parseInt(match[1]) + 1;
      }
      const jobNumber = `JOB-${String(nextNumber).padStart(3, '0')}`;

      // Create the job
      const { data: newJob, error } = await supabaseData
        .from('jobs')
        .insert({
          company_id: companyId,
          customer_id: quoteToSchedule.customer_id,
          quote_id: quoteToSchedule.id,
          job_number: jobNumber,
          job_type: 'Service',
          status: 'scheduled',
          scheduled_date: newJobDate,
          scheduled_time: newJobTime,
          assigned_tech: newJobTech || null,
          quoted_value: quoteToSchedule.total,
          notes: `Created from ${quoteToSchedule.quote_number}`
        })
        .select()
        .single();

      if (error) throw error;

      // Copy quote items to job items
      const { data: quoteItems } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quoteToSchedule.id);

      if (quoteItems && quoteItems.length > 0) {
        const jobItems = quoteItems.map(item => ({
          job_id: newJob.id,
          sku_id: item.sku_id,
          name: item.name || item.description,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total
        }));

        await supabaseData.from('job_items').insert(jobItems);
      }

      // Notify assigned tech (if not self)
      if (newJobTech && newJobTech !== currentUserId) {
        try {
          const custName = quoteToSchedule.customers?.name || quoteToSchedule.customers?.first_name || 'Customer';
          await supabaseData.from('notifications').insert({
            company_id: companyId,
            user_id: newJobTech,
            type: 'job_assigned',
            title: 'New Job Assigned',
            message: `${custName} - ${newJobDate} at ${newJobTime}`,
            data: { job_id: newJob.id, customer_id: quoteToSchedule.customer_id },
            read: false
          });
        } catch (notifErr) {
          // Don't fail job creation
        }
      }

      // Reload jobs
      loadJobs();
      setShowCreateJobModal(false);
      setQuoteToSchedule(null);
      setNewJobDate('');
      setNewJobTime('09:00');
      setNewJobTech('');

      // Navigate to the scheduled date
      setSelectedDate(new Date(newJobDate + 'T12:00:00'));

    } catch (error) {
      alert('Failed to create job: ' + error.message);
    } finally {
      setCreatingJob(false);
    }
  };

  // Load team members as technicians
  const loadTechnicians = async (retryCount = 0) => {
    try {
      // Offline: serve from cache
      if (!navigator.onLine && isOfflineModeEnabled()) {
        const cached = getCachedData('user_companies', companyId);
        if (cached) {
          const techColors = ['#F05A28', '#4ecca3', '#3498db', '#9b59b6', '#f39c12', '#e74c3c', '#1abc9c', '#e67e22'];
          const techs = cached
            .filter(member => {
              const companyRole = member.role;
              return companyRole === 'owner' || companyRole === 'admin' || companyRole === 'field_tech';
            })
            .map((member, idx) => ({
              id: member.user_id,
              name: member.user_profiles?.full_name || member.user_profiles?.email?.split('@')[0] || `Team Member ${idx + 1}`,
              role: member.role,
              color: techColors[idx % techColors.length]
            }));
          if (!techs.find(t => t.role === 'owner')) {
            techs.unshift({ id: 'owner', name: 'Owner', role: 'owner', color: '#F05A28' });
          }
          setTechnicians(techs);
          return;
        }
      }

      // Query user_companies and user_profiles separately to avoid relationship issues
      const { data: teamData, error: teamError } = await supabaseData
        .from('user_companies')
        .select('*')
        .eq('company_id', companyId);

      if (teamError) {
        if (retryCount < 1) {
          console.warn('PlanningScheduling: technicians query error, retrying with session refresh...', teamError.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 1000));
          return loadTechnicians(retryCount + 1);
        }
        throw teamError;
      }

      // Get user profiles for team members
      const userIds = (teamData || []).map(m => m.user_id);
      let profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabaseData
          .from('user_profiles')
          .select('id, full_name, email, role')
          .in('id', userIds);
        (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      }

      // Build technicians list - all team members except office-only roles get calendars
      const techColors = ['#F05A28', '#4ecca3', '#3498db', '#9b59b6', '#f39c12', '#e74c3c', '#1abc9c', '#e67e22'];
      const techs = (teamData || [])
        .filter(member => {
          const companyRole = member.role;
          return companyRole === 'owner' || companyRole === 'admin' || companyRole === 'field_tech' || companyRole === 'member';
        })
        .map((member, idx) => {
          const profile = profilesMap[member.user_id];
          return {
            id: member.user_id,
            name: profile?.full_name || profile?.email?.split('@')[0] || `Team Member ${idx + 1}`,
            role: member.role,
            color: techColors[idx % techColors.length]
          };
        });

      // Always include 'owner' placeholder for jobs without assigned tech
      if (!techs.find(t => t.role === 'owner')) {
        techs.unshift({ id: 'owner', name: 'Owner', role: 'owner', color: '#F05A28' });
      }

      // Cache on success
      if (isOfflineModeEnabled()) {
        cacheData('user_companies', companyId, teamData || []);
      }

      setTechnicians(techs);
    } catch (error) {
      // Fallback to owner only
      setTechnicians([{ id: 'owner', name: 'Owner', role: 'owner', color: '#F05A28' }]);
    }
  };

  // Load flex workers (Growth+ tiers)
  const loadFlexWorkers = async () => {
    try {
      // Load flex workers
      const { data: workers, error: workersError } = await supabaseData
        .from('flex_workers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (workersError) throw workersError;

      // Load flex days used this month
      const { data: daysUsed, error: daysError } = await supabaseData
        .rpc('get_flex_days_used', { p_company_id: companyId });

      if (!daysError) {
        setFlexDaysUsed(daysUsed || 0);
      }

      // Map flex workers with distinct colors
      const flexColors = ['#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#00bcd4', '#009688'];
      setFlexWorkers((workers || []).map((w, idx) => ({
        ...w,
        color: w.color || flexColors[idx % flexColors.length],
        isFlexWorker: true
      })));
    } catch (error) {
    }
  };

  // Activate flex worker for a date (uses a flex day)
  const activateFlexWorkerForJob = async (flexWorkerId, date) => {
    try {
      // Check if we have flex days remaining
      const flexDaysIncluded = getFlexDaysIncluded();
      if (flexDaysUsed >= flexDaysIncluded) {
        alert(`You've used all ${flexDaysIncluded} flex days this month. Upgrade for more flex days.`);
        return false;
      }

      // Check if this date is already activated for this worker
      const { data: existing } = await supabaseData
        .from('flex_activations')
        .select('id')
        .eq('flex_worker_id', flexWorkerId)
        .eq('activation_date', date)
        .single();

      if (!existing) {
        // Create activation
        const { error } = await supabaseData
          .from('flex_activations')
          .insert({
            company_id: companyId,
            flex_worker_id: flexWorkerId,
            activation_date: date
          });

        if (error) throw error;

        // Refresh flex days count
        loadFlexWorkers();
      }

      return true;
    } catch (error) {
      return true; // Allow the job assignment anyway
    }
  };

  // Load calendar events (meetings, training, etc.)
  const loadCalendarEvents = async () => {
    try {
      const { data: events, error } = await supabaseData
        .from('calendar_events')
        .select('*')
        .eq('company_id', companyId)
        .order('event_date', { ascending: true });

      if (error) throw error;
      setCalendarEvents(events || []);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    }
  };

  // Save a new calendar event
  const handleSaveEvent = async () => {
    if (!newEvent.title || !newEvent.event_date || !newEvent.start_time) {
      alert('Please fill in title, date, and time');
      return;
    }

    setSavingEvent(true);
    try {
      // Calculate end time from start_time and duration
      const [startHour, startMin] = newEvent.start_time.split(':').map(Number);
      const totalMinutes = startHour * 60 + startMin + newEvent.duration_minutes;
      const endHour = Math.floor(totalMinutes / 60);
      const endMin = totalMinutes % 60;
      const end_time = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;

      const { error } = await supabaseData
        .from('calendar_events')
        .insert({
          company_id: companyId,
          title: newEvent.title,
          description: newEvent.description || null,
          event_date: newEvent.event_date,
          start_time: newEvent.start_time,
          end_time: end_time,
          duration_minutes: newEvent.duration_minutes,
          assigned_to: newEvent.assigned_to || null,
          event_type: newEvent.event_type,
          color: newEvent.color
        });

      if (error) throw error;

      // Reload events
      await loadCalendarEvents();

      // Reset and close modal
      setNewEvent({
        title: '',
        event_date: '',
        start_time: '09:00',
        duration_minutes: 60,
        assigned_to: '',
        event_type: 'meeting',
        description: '',
        color: '#9b59b6'
      });
      setShowEventModal(false);
    } catch (error) {
      alert('Error saving event: ' + error.message);
    } finally {
      setSavingEvent(false);
    }
  };

  // Delete a calendar event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;

    try {
      const { error } = await supabaseData
        .from('calendar_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
      await loadCalendarEvents();
    } catch (error) {
      alert('Error deleting event: ' + error.message);
    }
  };

  const loadJobs = async (retryCount = 0) => {
    try {
      // Offline: serve from cache
      if (!navigator.onLine && isOfflineModeEnabled()) {
        const cached = getCachedData('jobs', companyId);
        if (cached) {
          const scheduled = cached.filter(j => j.scheduled_date && j.scheduled_time).map(mapJobForDisplay);
          const unscheduled = cached.filter(j => !j.scheduled_date || !j.scheduled_time).map(mapJobForDisplay);
          setScheduledJobs(scheduled);
          setUnscheduledJobs(unscheduled);
          setLoading(false);
          return;
        }
      }

      setLoading(true);

      // Load all jobs for the company
      const { data: jobs, error } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            id,
            first_name,
            last_name,
            name,
            phone,
            address,
            city,
            state,
            zip
          )
        `)
        .eq('company_id', companyId)
        .order('scheduled_date', { ascending: true });

      if (error) {
        if (retryCount < 1) {
          console.warn('PlanningScheduling: jobs query error, retrying with session refresh...', error.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 1000));
          return loadJobs(retryCount + 1);
        }
        throw error;
      }

      // Split into scheduled and unscheduled
      const scheduled = jobs.filter(j => j.scheduled_date && j.scheduled_time).map(mapJobForDisplay);
      const unscheduled = jobs.filter(j => !j.scheduled_date || !j.scheduled_time).map(mapJobForDisplay);

      // Cache on success
      if (isOfflineModeEnabled()) {
        cacheData('jobs', companyId, jobs || []);
      }

      setScheduledJobs(scheduled);
      setUnscheduledJobs(unscheduled);
    } catch (error) {
      // Fallback to cache
      if (isOfflineModeEnabled()) {
        const cached = getCachedData('jobs', companyId);
        if (cached) {
          const scheduled = cached.filter(j => j.scheduled_date && j.scheduled_time).map(mapJobForDisplay);
          const unscheduled = cached.filter(j => !j.scheduled_date || !j.scheduled_time).map(mapJobForDisplay);
          setScheduledJobs(scheduled);
          setUnscheduledJobs(unscheduled);
          setLoading(false);
          return;
        }
      }
      alert('Error loading jobs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown Customer';
    if (customer.first_name && customer.last_name) {
      return `${customer.last_name}, ${customer.first_name}`;
    }
    return customer.name || customer.first_name || customer.last_name || 'Unknown';
  };

  // Helper to map database job to display format
  const mapJobForDisplay = (job) => ({
    ...job,
    jobNumber: job.job_number,
    customerName: getCustomerName(job.customers),
    customerAddress: job.customers?.address || 'No address',
    customerPhone: job.customers?.phone || '',
    scheduledDate: job.scheduled_date,
    scheduledTime: job.scheduled_time,
    assignedTech: job.assigned_tech || 'owner',
    estimatedDuration: job.estimated_duration || 2,
    quotedValue: job.quoted_value || 0,
    isRecurringParent: isRecurringParent(job),
    isRecurringInstance: isRecurringInstance(job),
    recurrenceRule: job.recurrence_rule
  });

  // Time slots for day view (midnight to midnight, 15-minute intervals)
  const timeSlots = [];
  for (let i = 0; i <= 23; i++) {
    timeSlots.push(`${i}:00`);
    timeSlots.push(`${i}:15`);
    timeSlots.push(`${i}:30`);
    timeSlots.push(`${i}:45`);
  }

  // Split time slots into early / business / late groups
  const earlySlots = timeSlots.filter(t => parseInt(t.split(':')[0]) < businessHoursStart);
  const businessSlots = timeSlots.filter(t => {
    const h = parseInt(t.split(':')[0]);
    return h >= businessHoursStart && h < businessHoursEnd;
  });
  const lateSlots = timeSlots.filter(t => parseInt(t.split(':')[0]) >= businessHoursEnd);

  // Check if there are any jobs/events in off-hours for badge count
  const dateStr = getLocalDateString(selectedDate);
  const countOffHourItems = (slots) => {
    let count = 0;
    for (const time of slots) {
      const [hour, minutes] = time.split(':').map(Number);
      const slotStart = hour * 60 + minutes;
      count += scheduledJobs.filter(job => {
        if (job.scheduledDate !== dateStr || !job.scheduledTime) return false;
        const [jh, jm] = job.scheduledTime.split(':').map(Number);
        return Math.floor((jh * 60 + jm) / 15) * 15 === slotStart;
      }).length;
      count += calendarEvents.filter(event => {
        if (event.event_date !== dateStr || !event.start_time) return false;
        const [eh, em] = event.start_time.split(':').map(Number);
        return Math.floor((eh * 60 + em) / 15) * 15 === slotStart;
      }).length;
    }
    return count;
  };
  const earlyItemCount = countOffHourItems(earlySlots);
  const lateItemCount = countOffHourItems(lateSlots);

  // Format time for display (e.g., "2:00" -> "2:00 PM")
  const formatTimeSlot = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const getJobTypeColor = (type) => {
    const colors = {
      installation: '#3498db',
      repair: '#f39c12',
      maintenance: '#9b59b6'
    };
    return colors[type] || '#95a5a6';
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#3498db',
      'in-progress': '#f39c12',
      completed: '#4ecca3',
      unscheduled: '#95a5a6'
    };
    return colors[status] || '#95a5a6';
  };

  // Navigation functions
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Edit job handler
  const handleUpdateJob = async (jobId, updates) => {
    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('jobs')
        .update(updates)
        .eq('id', jobId);

      if (error) throw error;

      setEditingJob(null);
      setSelectedJob(null);
      await loadJobs();
    } catch (error) {
      alert('Error updating job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Cancel/delete job handler
  const handleCancelJob = async (job) => {
    if (!window.window.confirm(`Cancel job ${job.jobNumber || job.job_number}? This will remove it from the schedule.`)) return;

    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('jobs')
        .update({ status: 'cancelled', scheduled_date: null, scheduled_time: null })
        .eq('id', job.id);

      if (error) throw error;

      setSelectedJob(null);
      await loadJobs();
    } catch (error) {
      alert('Error cancelling job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Start job (move to in-progress)
  const handleStartJob = async (job) => {
    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('jobs')
        .update({ status: 'in-progress' })
        .eq('id', job.id);

      if (error) throw error;

      setSelectedJob(null);
      await loadJobs();
    } catch (error) {
      alert('Error starting job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Open completion modal
  const handleCompleteJob = (job) => {
    const jobType = job.jobType || job.job_type;

    // Check if this is a quote_visit job - use special modal
    if (jobType === 'quote_visit') {
      setSelectedQuoteVisitJob({
        id: job.id,
        job_number: job.jobNumber || job.job_number,
        customer_id: job.customerId || job.customer_id,
        title: job.title,
        description: job.description,
        address: job.customerAddress || job.address,
        quote_fee_amount: job.quote_fee_amount,
        job_type: jobType
      });
      setSelectedJob(null);
      setShowQuoteVisitModal(true);
      return;
    }

    // Need to get the full job data with customer info
    setSelectedJobForCompletion({
      id: job.id,
      job_number: job.jobNumber || job.job_number,
      customer_id: job.customerId || job.customer_id,
      customers: {
        name: job.customerName,
        first_name: job.customerName?.split(' ')[0],
        last_name: job.customerName?.split(' ').slice(1).join(' '),
        phone: job.customerPhone,
        address: job.customerAddress
      },
      job_type: jobType,
      quoted_value: job.quotedValue || job.quoted_value,
      notes: job.notes
    });
    setSelectedJob(null);
    setShowCompletionModal(true);
  };

  // Called after job completion
  const handleJobCompleted = () => {
    setShowCompletionModal(false);
    setSelectedJobForCompletion(null);
    loadJobs();
  };

  // View invoice for completed job
  const viewJobInvoice = async (job) => {
    try {
      const { data: invoice } = await supabaseData
        .from('invoices')
        .select('id')
        .eq('job_id', job.id)
        .single();

      if (invoice && onNavigate) {
        setSelectedJob(null);
        onNavigate('work-orders', { tab: 'invoices', invoiceId: invoice.id });
      } else {
        alert('No invoice found for this job');
      }
    } catch (error) {
      alert('Could not find invoice');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, techId, time, date) => {
    e.preventDefault();
    if (!draggedJob) return;

    try {
      const scheduleDate = date || getLocalDateString(selectedDate);

      // Check if this is a flex worker assignment
      const isFlexWorkerAssignment = techId.startsWith('flex-');
      if (isFlexWorkerAssignment) {
        const flexWorkerId = techId.replace('flex-', '');
        const canActivate = await activateFlexWorkerForJob(flexWorkerId, scheduleDate);
        if (!canActivate) {
          setDraggedJob(null);
          return;
        }
      }

      // Update job in database
      const { error } = await supabaseData
        .from('jobs')
        .update({
          scheduled_date: scheduleDate,
          scheduled_time: time,
          assigned_tech: techId,
          status: 'scheduled'
        })
        .eq('id', draggedJob.id);

      if (error) throw error;

      // Notify assigned tech (if not self or flex worker)
      if (techId && techId !== currentUserId && !techId.startsWith('flex-')) {
        try {
          const custName = draggedJob.customers?.name || draggedJob.customers?.first_name || 'Customer';
          await supabaseData.from('notifications').insert({
            company_id: companyId,
            user_id: techId,
            type: 'job_assigned',
            title: 'Job Assigned to You',
            message: `${custName} - ${scheduleDate} at ${time}`,
            data: { job_id: draggedJob.id, customer_id: draggedJob.customer_id },
            read: false
          });
        } catch (notifErr) {
          // Don't fail the reassignment
        }
      }

      // Reload jobs to reflect changes
      await loadJobs();
      setDraggedJob(null);
    } catch (error) {
      alert('Failed to schedule job: ' + error.message);
      setDraggedJob(null);
    }
  };

  const handleDropUnscheduled = (e) => {
    e.preventDefault();
    if (!draggedJob) return;

    // Remove from scheduled
    setScheduledJobs(prev => prev.filter(j => j.id !== draggedJob.id));

    // Add back to unscheduled
    const updatedJob = {
      ...draggedJob,
      scheduledDate: null,
      scheduledTime: null,
      assignedTech: null,
      status: 'unscheduled'
    };
    
    setUnscheduledJobs(prev => [...prev, updatedJob]);
    setDraggedJob(null);
  };

  // Get jobs for specific time slot and tech (matches jobs within 15-min window)
  const getJobsForSlot = (time, techId) => {
    const [hours, minutes] = time.split(':').map(Number);
    const slotStart = hours * 60 + minutes; // Convert to minutes
    const slotEnd = slotStart + 15; // 15-minute window

    // Find the tech object to check role
    const tech = allCalendars.find(t => t.id === techId);

    return scheduledJobs.filter(job => {
      const jobDate = job.scheduledDate;
      const currentDate = getLocalDateString(selectedDate);

      if (!job.scheduledTime || jobDate !== currentDate) {
        return false;
      }

      // Match tech: exact ID match, OR job is assigned to 'owner' placeholder and this tech is the owner
      const techMatch = job.assignedTech === techId ||
        (job.assignedTech === 'owner' && tech?.role === 'owner');
      if (!techMatch) {
        return false;
      }

      const [jobHours, jobMinutes] = job.scheduledTime.split(':').map(Number);
      const jobStart = jobHours * 60 + jobMinutes;

      // Job shows in slot if it starts within this 15-min window
      return jobStart >= slotStart && jobStart < slotEnd;
    });
  };

  // Get all jobs for a specific date (for week/month view)
  const getJobsForDate = (date) => {
    const dateStr = getLocalDateString(date);
    return scheduledJobs.filter(job => job.scheduledDate === dateStr);
  };

  // Get all calendar events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = getLocalDateString(date);
    return calendarEvents.filter(event => event.event_date === dateStr);
  };

  // Combine technicians and flex workers for calendar display
  const allCalendars = [
    ...technicians,
    ...(hasFlexWorkers() ? flexWorkers.map(fw => ({
      ...fw,
      id: `flex-${fw.id}`,
      isFlexWorker: true
    })) : [])
  ];

  // Filter techs - if none selected, show all
  const filteredTechs = selectedTechs.length === 0
    ? allCalendars
    : allCalendars.filter(t => selectedTechs.includes(t.id));

  // Toggle tech selection
  const toggleTechSelection = (techId) => {
    setSelectedTechs(prev => {
      if (prev.includes(techId)) {
        return prev.filter(id => id !== techId);
      } else {
        return [...prev, techId];
      }
    });
  };

  // Select all / clear all
  const selectAllTechs = () => setSelectedTechs([]);
  const clearAllTechs = () => setSelectedTechs(allCalendars.map(t => t.id));

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Month view: get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days in month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  // Week view: get days starting from today (or selected date if in future)
  // Only shows today and future days - no past days cluttering the scheduler
  const getWeekDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Start from today or selected date, whichever is later
    const startDate = new Date(Math.max(today.getTime(), date.getTime()));
    startDate.setHours(0, 0, 0, 0);

    // Get the remaining days in the week from the start date
    const dayOfWeek = startDate.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const daysToShow = daysUntilSunday + 1; // Include today/start date

    // Show at least 7 days, up to end of following week
    const totalDays = Math.max(7, daysToShow);

    return Array.from({ length: totalDays }, (_, i) => {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      return day;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Render a collapsible off-hours toggle button
  const renderOffHoursToggle = (label, isExpanded, setExpanded, itemCount, position) => {
    return (
      <div
        onClick={() => setExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 1rem',
          background: '#f0f0f0',
          cursor: 'pointer',
          borderBottom: '1px solid #e0e0e0',
          borderTop: position === 'bottom' ? '1px solid #e0e0e0' : 'none',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isExpanded ? <ChevronUp size={16} color="#666" /> : <ChevronDown size={16} color="#666" />}
          <span style={{ fontSize: '0.8rem', color: colors.textMuted, fontWeight: '600' }}>{label}</span>
        </div>
        {itemCount > 0 && (
          <span style={{
            fontSize: '0.7rem',
            background: colors.accent,
            color: 'white',
            borderRadius: '10px',
            padding: '0.15rem 0.5rem',
            fontWeight: '700'
          }}>
            {itemCount} scheduled
          </span>
        )}
      </div>
    );
  };

  // Desktop off-hours toggle (spans full grid width)
  const renderDesktopOffHoursToggle = (label, isExpanded, setExpanded, itemCount, position, techCount) => {
    return (
      <div
        onClick={() => setExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 1rem',
          background: '#f0f0f0',
          cursor: 'pointer',
          borderTop: '1px solid #e0e0e0',
          borderBottom: '1px solid #e0e0e0',
          userSelect: 'none',
          gridColumn: `1 / -1`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isExpanded ? <ChevronUp size={16} color="#666" /> : <ChevronDown size={16} color="#666" />}
          <span style={{ fontSize: '0.8rem', color: colors.textMuted, fontWeight: '600' }}>{label}</span>
        </div>
        {itemCount > 0 && (
          <span style={{
            fontSize: '0.7rem',
            background: colors.accent,
            color: 'white',
            borderRadius: '10px',
            padding: '0.15rem 0.5rem',
            fontWeight: '700'
          }}>
            {itemCount} scheduled
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      fontFamily: 'Archivo, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '2px solid #e0e0e0',
        padding: isMobile ? '1rem' : '2rem 3rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          maxWidth: '1800px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '1rem' : '0'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '0.5rem'
            }}>
              <Calendar size={isMobile ? 28 : 36} color="#1a1a1a" strokeWidth={2.5} />
              <h1 style={{
                fontSize: isMobile ? '1.5rem' : '2.5rem',
                fontWeight: '700',
                color: '#1a1a1a',
                letterSpacing: '-0.02em',
                margin: 0
              }}>
                {isMobile ? 'Schedule' : 'Planning & Scheduling'}
              </h1>
            </div>
            {!isMobile && (
            <p style={{
              color: '#2d2d2d',
              fontSize: '0.95rem',
              fontWeight: '500',
              margin: 0
            }}>
              Drag and drop jobs to schedule work
            </p>
            )}
          </div>

          {/* View Mode Toggles */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            background: '#f5f5f5',
            padding: '0.5rem',
            borderRadius: '8px',
            width: isMobile ? '100%' : 'auto'
          }}>
            {['day', 'week', 'month'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  flex: isMobile ? 1 : '0 0 auto',
                  padding: isMobile ? '0.75rem' : '0.75rem 1.5rem',
                  background: viewMode === mode ? colors.bgCard : 'transparent',
                  color: viewMode === mode ? '#4ecca3' : colors.textMuted,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.2s',
                  minHeight: '44px'
                }}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1800px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem 3rem'
      }}>
        {/* Date Navigation & Filters */}
        <div style={{
          background: 'white',
          padding: isMobile ? '1rem' : '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '1rem' : '0'
        }}>
          {/* Date Navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            justifyContent: isMobile ? 'space-between' : 'flex-start'
          }}>
            <button
              onClick={() => navigateDate(-1)}
              style={{
                padding: '0.5rem',
                background: '#f5f5f5',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s',
                minWidth: '44px',
                minHeight: '44px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f5f5f5'}
            >
              <ChevronLeft size={20} color="#1a1a1a" />
            </button>

            <div style={{
              fontSize: isMobile ? '1rem' : '1.25rem',
              fontWeight: '600',
              color: '#1a1a1a',
              minWidth: isMobile ? 'auto' : '300px',
              flex: isMobile ? 1 : 'none',
              textAlign: 'center'
            }}>
              {viewMode === 'day' && (isMobile ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : formatDate(selectedDate))}
              {viewMode === 'week' && `Week of ${selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
              {viewMode === 'month' && selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>

            <button
              onClick={() => navigateDate(1)}
              style={{
                padding: '0.5rem',
                background: '#f5f5f5',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f5f5f5'}
            >
              <ChevronRight size={20} color="#1a1a1a" />
            </button>

            <button
              onClick={goToToday}
              style={{
                padding: '0.5rem 1rem',
                background: colors.bgCard,
                color: '#4ecca3',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginLeft: '1rem'
              }}
            >
              Today
            </button>
          </div>

          {/* Tech Filter - Multi-Select */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setShowTechFilter(!showTechFilter)}
              style={{
                padding: '0.75rem 1rem',
                background: selectedTechs.length > 0 ? `${colors.accent}20` : '#f5f5f5',
                border: selectedTechs.length > 0 ? `2px solid ${colors.accent}` : '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '500',
                color: '#1a1a1a',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minWidth: '180px'
              }}
            >
              <Filter size={18} />
              {selectedTechs.length === 0 ? (
                `All Calendars (${technicians.length})`
              ) : (
                `${selectedTechs.length} of ${technicians.length} Selected`
              )}
            </button>

            {/* Dropdown */}
            {showTechFilter && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.5rem',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '280px',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                {/* Header */}
                <div style={{
                  padding: '1rem',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f9f9f9',
                  borderRadius: '10px 10px 0 0'
                }}>
                  <span style={{ fontWeight: '600', color: '#1a1a1a' }}>Select Calendars</span>
                  <button
                    onClick={() => setShowTechFilter(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem'
                    }}
                  >
                    <X size={18} color="#666" />
                  </button>
                </div>

                {/* Quick Actions */}
                <div style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={selectAllTechs}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      background: selectedTechs.length === 0 ? '#4ecca3' : '#f5f5f5',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: selectedTechs.length === 0 ? 'white' : colors.textMuted,
                      cursor: 'pointer'
                    }}
                  >
                    Show All
                  </button>
                  <button
                    onClick={clearAllTechs}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      background: '#f5f5f5',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: colors.textMuted,
                      cursor: 'pointer'
                    }}
                  >
                    Clear All
                  </button>
                </div>

                {/* Tech List */}
                <div style={{ padding: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: colors.textMuted, padding: '0.5rem', fontWeight: '600' }}>
                    TEAM MEMBERS
                  </div>
                  {technicians.map(tech => (
                    <label
                      key={tech.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: (selectedTechs.length === 0 || selectedTechs.includes(tech.id)) ? `${tech.color}15` : 'transparent',
                        marginBottom: '0.25rem',
                        transition: 'all 0.15s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTechs.length === 0 || selectedTechs.includes(tech.id)}
                        onChange={() => toggleTechSelection(tech.id)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        border: `2px solid ${tech.color}`,
                        background: (selectedTechs.length === 0 || selectedTechs.includes(tech.id)) ? tech.color : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {(selectedTechs.length === 0 || selectedTechs.includes(tech.id)) && (
                          <Check size={14} color="white" strokeWidth={3} />
                        )}
                      </div>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: tech.color
                      }} />
                      <span style={{
                        flex: 1,
                        fontWeight: '500',
                        color: '#1a1a1a'
                      }}>
                        {tech.name}
                      </span>
                      {tech.role && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: colors.textMuted,
                          textTransform: 'capitalize'
                        }}>
                          {tech.role.replace('_', ' ')}
                        </span>
                      )}
                    </label>
                  ))}

                  {/* Flex Workers Section (Growth+ only) */}
                  {hasFlexWorkers() && flexWorkers.length > 0 && (
                    <>
                      <div style={{
                        fontSize: '0.75rem',
                        color: colors.textMuted,
                        padding: '0.75rem 0.5rem 0.5rem',
                        fontWeight: '600',
                        borderTop: '1px solid #e0e0e0',
                        marginTop: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <UserPlus size={14} />
                          FLEX WORKERS (1099)
                        </span>
                        <span style={{
                          fontSize: '0.7rem',
                          background: flexDaysUsed < getFlexDaysIncluded() ? '#4ecca320' : '#e74c3c20',
                          color: flexDaysUsed < getFlexDaysIncluded() ? '#4ecca3' : '#e74c3c',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px'
                        }}>
                          {getFlexDaysIncluded() - flexDaysUsed} days left
                        </span>
                      </div>
                      {flexWorkers.map(fw => (
                        <label
                          key={`flex-${fw.id}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: (selectedTechs.length === 0 || selectedTechs.includes(`flex-${fw.id}`)) ? `${fw.color}15` : 'transparent',
                            marginBottom: '0.25rem',
                            transition: 'all 0.15s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTechs.length === 0 || selectedTechs.includes(`flex-${fw.id}`)}
                            onChange={() => toggleTechSelection(`flex-${fw.id}`)}
                            style={{ display: 'none' }}
                          />
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '6px',
                            border: `2px solid ${fw.color}`,
                            background: (selectedTechs.length === 0 || selectedTechs.includes(`flex-${fw.id}`)) ? fw.color : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {(selectedTechs.length === 0 || selectedTechs.includes(`flex-${fw.id}`)) && (
                              <Check size={14} color="white" strokeWidth={3} />
                            )}
                          </div>
                          <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: fw.color
                          }} />
                          <span style={{
                            flex: 1,
                            fontWeight: '500',
                            color: '#1a1a1a'
                          }}>
                            {fw.name}
                          </span>
                          <span style={{
                            fontSize: '0.7rem',
                            color: '#9b59b6',
                            background: '#9b59b620',
                            padding: '0.15rem 0.4rem',
                            borderRadius: '4px'
                          }}>
                            Flex
                          </span>
                        </label>
                      ))}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div style={{
                  padding: '1rem',
                  borderTop: '1px solid #e0e0e0',
                  background: '#f9f9f9',
                  borderRadius: '0 0 10px 10px'
                }}>
                  <button
                    onClick={() => setShowTechFilter(false)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgCard,
                      color: '#4ecca3',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            )}

            {/* Schedule Event Button */}
            <button
              onClick={() => {
                setNewEvent(prev => ({
                  ...prev,
                  event_date: getLocalDateString(selectedDate)
                }));
                setShowEventModal(true);
              }}
              style={{
                padding: '0.75rem 1rem',
                background: '#9b59b6',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginLeft: '0.5rem'
              }}
            >
              <Plus size={18} />
              {isMobile ? 'Event' : 'Schedule Event'}
            </button>
          </div>
        </div>

        {/* DAY VIEW */}
        {viewMode === 'day' && (
          <div>
            {/* Mobile: Full Day Timeline (Scrollable) */}
            {isMobile ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'hidden'
              }}>
                {/* Scrollable timeline */}
                <div style={{
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  {/* Early hours (before business hours) - collapsed by default */}
                  {earlySlots.length > 0 && renderOffHoursToggle(
                    `Early Hours (12:00 AM - ${formatTimeSlot(businessHoursStart + ':00')})`,
                    showEarlyHours, setShowEarlyHours, earlyItemCount, 'top'
                  )}

                  {/* Time slots - 15 minute intervals */}
                  {(showEarlyHours ? [...earlySlots, ...businessSlots] : businessSlots).concat(
                    showLateHours ? lateSlots : []
                  ).map(time => {
                    const [hour, minutes] = time.split(':').map(Number);
                    const dateStr = getLocalDateString(selectedDate);
                    const slotStartMinutes = hour * 60 + minutes;

                    // Find jobs that start in this slot
                    const slotJobs = scheduledJobs.filter(job => {
                      if (job.scheduledDate !== dateStr || !job.scheduledTime) return false;
                      const [jobHour, jobMin] = job.scheduledTime.split(':').map(Number);
                      // Round job time to nearest 15-min slot
                      const jobSlotMinutes = Math.floor((jobHour * 60 + jobMin) / 15) * 15;
                      return jobSlotMinutes === slotStartMinutes;
                    });

                    // Check if any job spans into this slot (started earlier but still going)
                    const spanningJobs = scheduledJobs.filter(job => {
                      if (job.scheduledDate !== dateStr || !job.scheduledTime) return false;
                      const [jobHour, jobMin] = job.scheduledTime.split(':').map(Number);
                      const jobStartMinutes = jobHour * 60 + jobMin;
                      const durationMinutes = job.estimatedDuration || 60;
                      const jobEndMinutes = jobStartMinutes + durationMinutes;
                      // Job spans this slot if it started before and ends after this slot starts
                      return jobStartMinutes < slotStartMinutes && jobEndMinutes > slotStartMinutes;
                    });

                    // Find calendar events that start in this slot
                    const slotEvents = calendarEvents.filter(event => {
                      if (event.event_date !== dateStr || !event.start_time) return false;
                      const [eventHour, eventMin] = event.start_time.split(':').map(Number);
                      const eventSlotMinutes = Math.floor((eventHour * 60 + eventMin) / 15) * 15;
                      return eventSlotMinutes === slotStartMinutes;
                    });

                    const now = new Date();
                    const currentMinutes = now.getHours() * 60 + now.getMinutes();
                    const isNowSlot = Math.floor(currentMinutes / 15) * 15 === slotStartMinutes &&
                      selectedDate.toDateString() === now.toDateString();

                    // Only show border on the hour
                    const isHourStart = minutes === 0;

                    return (
                      <div
                        key={time}
                        style={{
                          display: 'flex',
                          borderBottom: isHourStart ? '1px solid #e0e0e0' : '1px solid #f0f0f0',
                          minHeight: isHourStart ? '60px' : '40px',
                          background: isNowSlot ? '#fffbeb' : 'white'
                        }}
                      >
                        {/* Time label */}
                        <div style={{
                          width: '70px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: isHourStart ? '600' : '400',
                          color: isNowSlot ? '#f59e0b' : (isHourStart ? colors.textMuted : colors.textMuted),
                          background: '#f9f9f9',
                          borderRight: '1px solid #e0e0e0',
                          fontFamily: '"JetBrains Mono", monospace',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          paddingTop: '0.5rem'
                        }}>
                          {formatTimeSlot(time)}
                        </div>

                        {/* Content area */}
                        <div style={{
                          flex: 1,
                          padding: '0.25rem',
                          position: 'relative',
                          minHeight: isHourStart ? '60px' : '40px'
                        }}>
                          {/* Show spanning job indicator */}
                          {spanningJobs.length > 0 && slotJobs.length === 0 && (
                            <div style={{
                              position: 'absolute',
                              left: '0.25rem',
                              top: 0,
                              bottom: 0,
                              width: '3px',
                              background: getJobTypeColor(spanningJobs[0].jobType),
                              borderRadius: '2px'
                            }} />
                          )}

                          {/* Jobs starting this slot */}
                          {slotJobs.map(job => {
                            const durationHours = (job.estimatedDuration || 60) / 60;
                            return (
                              <div
                                key={job.id}
                                onClick={() => setSelectedJob(job)}
                                style={{
                                  background: getJobTypeColor(job.jobType),
                                  padding: '0.75rem',
                                  borderRadius: '8px',
                                  marginBottom: '0.5rem',
                                  cursor: 'pointer',
                                  border: `2px solid ${getStatusColor(job.status)}`,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  marginBottom: '0.25rem'
                                }}>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(255,255,255,0.9)',
                                    fontWeight: '600',
                                    fontFamily: '"JetBrains Mono", monospace'
                                  }}>
                                    {job.scheduledTime} ({durationHours.toFixed(1)}h)
                                  </div>
                                  <div style={{
                                    fontSize: '0.7rem',
                                    color: 'rgba(255,255,255,0.8)',
                                    fontFamily: '"JetBrains Mono", monospace'
                                  }}>
                                    {job.jobNumber}
                                  </div>
                                </div>
                                <div style={{
                                  fontSize: '1rem',
                                  fontWeight: '700',
                                  color: 'white',
                                  marginBottom: '0.25rem'
                                }}>
                                  {job.customerName}
                                </div>
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: 'rgba(255,255,255,0.9)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  <MapPin size={12} />
                                  {job.customerAddress?.split(',')[0]}
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '0.5rem'
                                }}>
                                  {job.customerPhone && (
                                    <a
                                      href={`tel:${job.customerPhone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        color: 'white',
                                        fontSize: '0.8rem',
                                        textDecoration: 'none',
                                        background: 'rgba(0,0,0,0.2)',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px'
                                      }}
                                    >
                                      <Phone size={12} />
                                      Call
                                    </a>
                                  )}
                                  <div style={{
                                    fontSize: '0.9rem',
                                    fontWeight: '700',
                                    color: '#4ecca3',
                                    background: 'rgba(0,0,0,0.2)',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px'
                                  }}>
                                    ${job.quotedValue}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Calendar events starting this slot */}
                          {slotEvents.map(event => (
                            <div
                              key={event.id}
                              style={{
                                background: event.color || '#9b59b6',
                                padding: '0.75rem',
                                borderRadius: '8px',
                                marginBottom: '0.5rem',
                                cursor: 'pointer',
                                border: '2px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}
                              onClick={() => {
                                if (window.confirm(`Delete event "${event.title}"?`)) {
                                  handleDeleteEvent(event.id);
                                }
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '0.25rem'
                              }}>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: 'rgba(255,255,255,0.9)',
                                  fontWeight: '600',
                                  fontFamily: '"JetBrains Mono", monospace'
                                }}>
                                  {event.start_time} ({event.duration_minutes}min)
                                </div>
                                <div style={{
                                  fontSize: '0.7rem',
                                  color: 'rgba(255,255,255,0.8)',
                                  textTransform: 'uppercase'
                                }}>
                                  {event.event_type}
                                </div>
                              </div>
                              <div style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: 'white'
                              }}>
                                {event.title}
                              </div>
                              {event.description && (
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: 'rgba(255,255,255,0.8)',
                                  marginTop: '0.25rem'
                                }}>
                                  {event.description}
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Empty slot - only show dash on the hour for cleaner look */}
                          {slotJobs.length === 0 && spanningJobs.length === 0 && slotEvents.length === 0 && isHourStart && (
                            <div style={{
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#ddd',
                              fontSize: '0.7rem'
                            }}>
                              —
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Late hours (after business hours) - collapsed by default */}
                  {lateSlots.length > 0 && renderOffHoursToggle(
                    `Late Hours (${formatTimeSlot(businessHoursEnd + ':00')} - 12:00 AM)`,
                    showLateHours, setShowLateHours, lateItemCount, 'bottom'
                  )}
                </div>
              </div>
            ) : (
              /* Desktop: Time Grid with sticky headers */
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'auto',
                maxHeight: '75vh',
                position: 'relative'
              }}>
                {/* Header with tech names - STICKY */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `100px repeat(${filteredTechs.length}, minmax(150px, 1fr))`,
                  background: colors.bgCard,
                  padding: '1rem',
                  gap: '1rem',
                  position: 'sticky',
                  top: 0,
                  zIndex: 20
                }}>
                  <div style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: colors.textMuted,
                    position: 'sticky',
                    left: 0,
                    background: colors.bgCard,
                    zIndex: 21
                  }}>
                    Time
                  </div>
                  {filteredTechs.map(tech => (
                    <div
                      key={tech.id}
                      style={{
                        fontSize: '1rem',
                        fontWeight: '700',
                        color: tech.color,
                        textAlign: 'center'
                      }}
                    >
                      {tech.name}
                    </div>
                  ))}
                </div>

                {/* Time slots */}
                <div>
                  {/* Early hours toggle */}
                  {earlySlots.length > 0 && renderDesktopOffHoursToggle(
                    `Early Hours (12:00 AM - ${formatTimeSlot(businessHoursStart + ':00')})`,
                    showEarlyHours, setShowEarlyHours, earlyItemCount, 'top', filteredTechs.length
                  )}

                  {(showEarlyHours ? [...earlySlots, ...businessSlots] : businessSlots).concat(
                    showLateHours ? lateSlots : []
                  ).map((time, idx) => (
                    <div
                      key={time}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `100px repeat(${filteredTechs.length}, minmax(150px, 1fr))`,
                        borderTop: '1px solid #e0e0e0',
                        minHeight: '50px',
                        gap: '1rem'
                      }}
                    >
                      {/* Time label - STICKY on left */}
                      <div style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: colors.textMuted,
                        background: '#f9f9f9',
                        display: 'flex',
                        alignItems: 'flex-start',
                        fontFamily: '"JetBrains Mono", monospace',
                        position: 'sticky',
                        left: 0,
                        zIndex: 10
                      }}>
                        {formatTimeSlot(time)}
                      </div>

                      {/* Tech columns */}
                      {filteredTechs.map(tech => {
                        const jobs = getJobsForSlot(time, tech.id);
                        // Get events for this slot (assigned to this tech or unassigned)
                        const dateStr = getLocalDateString(selectedDate);
                        const slotEvents = calendarEvents.filter(event => {
                          if (event.event_date !== dateStr || !event.start_time) return false;
                          const [eventHour, eventMin] = event.start_time.split(':').map(Number);
                          const [slotHour, slotMin] = time.split(':').map(Number);
                          const eventSlotMinutes = Math.floor((eventHour * 60 + eventMin) / 15) * 15;
                          const slotStartMinutes = slotHour * 60 + slotMin;
                          if (eventSlotMinutes !== slotStartMinutes) return false;
                          // Show if assigned to this tech or unassigned (show in first column only for unassigned)
                          return event.assigned_to === tech.id || (!event.assigned_to && tech.id === filteredTechs[0]?.id);
                        });
                        return (
                          <div
                            key={tech.id}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, tech.id, time, getLocalDateString(selectedDate))}
                            style={{
                              padding: '0.25rem',
                              background: jobs.length > 0 ? '#f9f9f9' : 'white',
                              borderLeft: '1px solid #e0e0e0',
                              position: 'relative',
                              minHeight: '50px',
                              transition: 'background 0.2s'
                            }}
                          >
                            {jobs.map(job => {
                              // Calculate height based on duration (each 15-min slot = 50px)
                              // estimatedDuration is stored in MINUTES (60, 90, 120, etc.)
                              const durationMinutes = job.estimatedDuration || 60; // default 1 hour
                              const slotsNeeded = durationMinutes / 15; // 4 slots per hour
                              const height = Math.max((slotsNeeded * 50) - 10, 40); // 50px per slot, min 40px

                              return (
                              <div
                                key={job.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, job)}
                                onClick={() => setSelectedJob(job)}
                                style={{
                                  background: getJobTypeColor(job.jobType),
                                  padding: '0.75rem',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  border: `2px solid ${getStatusColor(job.status)}`,
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s',
                                  height: `${height}px`,
                                  position: 'absolute',
                                  top: '5px',
                                  left: '5px',
                                  right: '5px',
                                  zIndex: 10,
                                  overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.02)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  marginBottom: '0.25rem'
                                }}>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(255,255,255,0.9)',
                                    fontWeight: '700',
                                    fontFamily: '"JetBrains Mono", monospace'
                                  }}>
                                    {job.jobNumber}
                                  </span>
                                  {(job.isRecurringParent || job.isRecurringInstance) && (
                                    <RefreshCw size={12} color="#9b59b6" style={{ flexShrink: 0 }} />
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '700',
                                  color: 'white',
                                  marginBottom: '0.5rem'
                                }}>
                                  {job.customerName}
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: 'rgba(255,255,255,0.9)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  marginBottom: '0.25rem'
                                }}>
                                  <MapPin size={12} />
                                  {job.customerAddress.split(',')[0]}
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '0.5rem'
                                }}>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(255,255,255,0.9)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    fontFamily: '"JetBrains Mono", monospace'
                                  }}>
                                    <Clock size={12} />
                                    {(job.estimatedDuration / 60).toFixed(1)}h
                                  </div>
                                  <div style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '700',
                                    color: 'white',
                                    fontFamily: '"JetBrains Mono", monospace'
                                  }}>
                                    ${job.quotedValue}
                                  </div>
                                </div>
                              </div>
                            )})}

                            {/* Calendar events for this slot */}
                            {slotEvents.map(event => {
                              const durationMinutes = event.duration_minutes || 60;
                              const slotsNeeded = durationMinutes / 15;
                              const height = Math.max((slotsNeeded * 50) - 10, 40);
                              return (
                                <div
                                  key={event.id}
                                  onClick={() => {
                                    if (window.confirm(`Delete event "${event.title}"?`)) {
                                      handleDeleteEvent(event.id);
                                    }
                                  }}
                                  style={{
                                    background: event.color || '#9b59b6',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                    height: `${height}px`,
                                    position: 'absolute',
                                    top: jobs.length > 0 ? `${(jobs.length * 50) + 5}px` : '5px',
                                    left: '5px',
                                    right: '5px',
                                    zIndex: 5,
                                    overflow: 'hidden'
                                  }}
                                >
                                  <div style={{
                                    fontSize: '0.7rem',
                                    color: 'rgba(255,255,255,0.8)',
                                    textTransform: 'uppercase',
                                    marginBottom: '0.25rem'
                                  }}>
                                    {event.event_type}
                                  </div>
                                  <div style={{
                                    fontSize: '0.9rem',
                                    fontWeight: '700',
                                    color: 'white'
                                  }}>
                                    {event.title}
                                  </div>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(255,255,255,0.9)',
                                    marginTop: '0.25rem'
                                  }}>
                                    {event.duration_minutes}min
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {/* Late hours toggle */}
                  {lateSlots.length > 0 && renderDesktopOffHoursToggle(
                    `Late Hours (${formatTimeSlot(businessHoursEnd + ':00')} - 12:00 AM)`,
                    showLateHours, setShowLateHours, lateItemCount, 'bottom', filteredTechs.length
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* WEEK VIEW */}
        {viewMode === 'week' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflowX: 'auto'
          }}>
            {isMobile ? (
              /* Mobile: Vertical list of days */
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {getWeekDays(selectedDate).map((day, idx) => {
                  const jobs = getJobsForDate(day);
                  const events = getEventsForDate(day);
                  const todayHighlight = isToday(day);

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        setSelectedDate(day);
                        setViewMode('day');
                      }}
                      style={{
                        padding: '1rem',
                        borderBottom: '1px solid #e0e0e0',
                        background: todayHighlight ? '#f0fdf4' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: jobs.length > 0 ? '0.75rem' : 0
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            background: todayHighlight ? '#4ecca3' : '#f5f5f5',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              fontSize: '0.65rem',
                              fontWeight: '600',
                              color: todayHighlight ? 'white' : colors.textMuted,
                              textTransform: 'uppercase'
                            }}>
                              {day.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                            <div style={{
                              fontSize: '1.25rem',
                              fontWeight: '700',
                              color: todayHighlight ? 'white' : '#1a1a1a'
                            }}>
                              {day.getDate()}
                            </div>
                          </div>
                          <div>
                            <div style={{
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: '#1a1a1a'
                            }}>
                              {day.toLocaleDateString('en-US', { weekday: 'long' })}
                            </div>
                            <div style={{
                              fontSize: '0.85rem',
                              color: colors.textMuted
                            }}>
                              {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                              {events.length > 0 && ` • ${events.length} ${events.length === 1 ? 'event' : 'events'}`}
                            </div>
                          </div>
                        </div>
                        <ChevronRight size={20} color="#999" />
                      </div>

                      {(jobs.length > 0 || events.length > 0) && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          marginLeft: '60px'
                        }}>
                          {jobs.slice(0, 2).map(job => (
                            <div
                              key={job.id}
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: getJobTypeColor(job.jobType),
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <div>
                                <span style={{ fontWeight: '600' }}>{job.scheduledTime}</span>
                                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>{job.customerName}</span>
                              </div>
                            </div>
                          ))}
                          {jobs.length > 2 && (
                            <div style={{ fontSize: '0.8rem', color: colors.textMuted, fontWeight: '500' }}>
                              +{jobs.length - 2} more jobs
                            </div>
                          )}
                          {/* Calendar events */}
                          {events.slice(0, 2).map(event => (
                            <div
                              key={event.id}
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: event.color || '#9b59b6',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <div>
                                <span style={{ fontWeight: '600' }}>{event.start_time}</span>
                                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>{event.title}</span>
                              </div>
                            </div>
                          ))}
                          {events.length > 2 && (
                            <div style={{ fontSize: '0.8rem', color: colors.textMuted, fontWeight: '500' }}>
                              +{events.length - 2} more events
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Desktop: Dynamic column grid with sticky headers */
              (() => {
                const weekDays = getWeekDays(selectedDate);
                return (
                  <div style={{
                    maxHeight: '75vh',
                    overflow: 'auto',
                    position: 'relative'
                  }}>
                    {/* Sticky date headers */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.min(weekDays.length, 7)}, minmax(140px, 1fr))`,
                      gap: '1px',
                      background: colors.bgCard,
                      position: 'sticky',
                      top: 0,
                      zIndex: 20
                    }}>
                      {weekDays.slice(0, 7).map((day, idx) => {
                        const todayHighlight = isToday(day);
                        const jobs = getJobsForDate(day);
                        const events = getEventsForDate(day);
                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              setSelectedDate(day);
                              setViewMode('day');
                            }}
                            style={{
                              background: todayHighlight ? '#2d4a3e' : colors.bgCard,
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              textAlign: 'center',
                              borderBottom: todayHighlight ? '3px solid #4ecca3' : 'none'
                            }}
                          >
                            <div style={{
                              fontSize: '0.7rem',
                              color: colors.textMuted,
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              marginBottom: '0.25rem'
                            }}>
                              {day.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                            <div style={{
                              fontSize: '1.25rem',
                              fontWeight: '700',
                              color: todayHighlight ? '#4ecca3' : '#fff',
                              fontFamily: '"JetBrains Mono", monospace'
                            }}>
                              {day.getDate()}
                            </div>
                            <div style={{
                              fontSize: '0.7rem',
                              color: todayHighlight ? '#4ecca3' : colors.textMuted,
                              marginTop: '0.25rem'
                            }}>
                              {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                              {events.length > 0 && ` • ${events.length}`}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Scrollable job content */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.min(weekDays.length, 7)}, minmax(140px, 1fr))`,
                      gap: '1px',
                      background: colors.border
                    }}>
                      {weekDays.slice(0, 7).map((day, idx) => {
                        const jobs = getJobsForDate(day);
                        const events = getEventsForDate(day);
                        const todayHighlight = isToday(day);

                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              setSelectedDate(day);
                              setViewMode('day');
                            }}
                            style={{
                              background: todayHighlight ? '#f0fdf4' : 'white',
                              padding: '1rem',
                              minHeight: '200px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = todayHighlight ? '#dcfce7' : '#f9f9f9'}
                            onMouseLeave={(e) => e.currentTarget.style.background = todayHighlight ? '#f0fdf4' : 'white'}
                          >
                            {/* Jobs for this day */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem'
                            }}>
                              {jobs.slice(0, 5).map(job => (
                                <div
                                  key={job.id}
                                  style={{
                                    padding: '0.5rem',
                                    background: getJobTypeColor(job.jobType),
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    color: 'white',
                                    fontWeight: '600'
                                  }}
                                >
                                  <div>{job.scheduledTime}</div>
                                  <div style={{ opacity: 0.9 }}>{job.customerName}</div>
                                </div>
                              ))}
                              {jobs.length > 5 && (
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: colors.textMuted,
                                  textAlign: 'center',
                                  fontWeight: '600'
                                }}>
                                  +{jobs.length - 5} more jobs
                                </div>
                              )}
                              {/* Calendar events */}
                              {events.slice(0, 3).map(event => (
                                <div
                                  key={event.id}
                                  style={{
                                    padding: '0.5rem',
                                    background: event.color || '#9b59b6',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    color: 'white',
                                    fontWeight: '600'
                                  }}
                                >
                                  <div>{event.start_time}</div>
                                  <div style={{ opacity: 0.9 }}>{event.title}</div>
                                </div>
                              ))}
                              {events.length > 3 && (
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: colors.textMuted,
                                  textAlign: 'center',
                                  fontWeight: '600'
                                }}>
                                  +{events.length - 3} more events
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        )}

        {/* MONTH VIEW */}
        {viewMode === 'month' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflowX: 'auto'
          }}>
            {/* Day headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              background: colors.bgCard,
              padding: isMobile ? '0.5rem' : '1rem',
              gap: '1px'
            }}>
              {(isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    fontWeight: '700',
                    color: colors.accent,
                    textAlign: 'center'
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '1px',
              background: colors.border
            }}>
              {getDaysInMonth(selectedDate).map((day, idx) => {
                if (!day) {
                  return <div key={idx} style={{ background: '#f9f9f9', minHeight: isMobile ? '48px' : '100px' }} />;
                }

                const jobs = getJobsForDate(day);
                const events = getEventsForDate(day);
                const todayHighlight = isToday(day);

                return (
                  <div
                    key={idx}
                    onClick={() => {
                      setSelectedDate(day);
                      setViewMode('day');
                    }}
                    style={{
                      background: 'white',
                      padding: isMobile ? '0.25rem' : '0.75rem',
                      minHeight: isMobile ? '48px' : '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: todayHighlight ? '2px solid #4ecca3' : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isMobile ? 'center' : 'flex-start'
                    }}
                    onMouseEnter={(e) => !isMobile && (e.currentTarget.style.background = '#f9f9f9')}
                    onMouseLeave={(e) => !isMobile && (e.currentTarget.style.background = 'white')}
                  >
                    <div style={{
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      fontWeight: '700',
                      color: todayHighlight ? '#4ecca3' : '#1a1a1a',
                      marginBottom: isMobile ? '0.125rem' : '0.5rem',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {day.getDate()}
                    </div>
                    {(jobs.length > 0 || events.length > 0) && (
                      isMobile ? (
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {jobs.length > 0 && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: colors.accent
                            }} />
                          )}
                          {events.length > 0 && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#9b59b6'
                            }} />
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {jobs.length > 0 && (
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: colors.textMuted,
                              background: '#f5f5f5',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}>
                              {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                            </div>
                          )}
                          {events.length > 0 && (
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#fff',
                              background: '#9b59b6',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}>
                              {events.length} {events.length === 1 ? 'event' : 'events'}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Unscheduled Jobs Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: isMobile ? '1rem' : '2rem',
          marginTop: '2rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <AlertCircle size={isMobile ? 20 : 24} color="#f39c12" />
            <h2 style={{
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              fontWeight: '700',
              color: '#1a1a1a',
              margin: 0
            }}>
              Unscheduled ({unscheduledJobs.length})
            </h2>
          </div>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDropUnscheduled}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              minHeight: unscheduledJobs.length === 0 ? '80px' : 'auto',
              padding: isMobile ? '0.75rem' : '1rem',
              background: '#f9f9f9',
              borderRadius: '8px',
              border: '2px dashed #e0e0e0'
            }}
          >
            {unscheduledJobs.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: '0.95rem',
                fontWeight: '600',
                gridColumn: '1 / -1',
                padding: '2rem'
              }}>
                All jobs are scheduled!
              </div>
            ) : (
              unscheduledJobs.map(job => (
                <div
                  key={job.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, job)}
                  style={{
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    cursor: 'move',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontSize: '0.75rem',
                    color: colors.textMuted,
                    fontWeight: '700',
                    marginBottom: '0.5rem',
                    fontFamily: '"JetBrains Mono", monospace'
                  }}>
                    {job.jobNumber}
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    marginBottom: '0.5rem'
                  }}>
                    {job.customerName}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: colors.textMuted,
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <MapPin size={14} />
                    {job.customerAddress.split(',')[0]}
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      background: getJobTypeColor(job.jobType),
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {job.jobType}
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: colors.textMuted,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      <Clock size={14} />
                      {job.estimatedDuration}h
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedJob(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: colors.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>
                  {selectedJob.jobNumber}
                </div>
                <h2 style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', color: '#1a1a1a' }}>
                  {selectedJob.customerName}
                </h2>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} color="#666" />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Status Badge */}
              <div style={{
                display: 'inline-flex',
                padding: '0.5rem 1rem',
                background: getStatusColor(selectedJob.status),
                color: '#fff',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: '600',
                alignSelf: 'flex-start',
                textTransform: 'capitalize'
              }}>
                {selectedJob.status}
              </div>

              {/* Schedule Info */}
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                padding: '1rem',
                background: '#f5f5f5',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={18} color="#666" />
                  <span style={{ color: '#1a1a1a' }}>{selectedJob.scheduledDate}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock size={18} color="#666" />
                  <span style={{ color: '#1a1a1a' }}>{selectedJob.scheduledTime}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: colors.textMuted }}>{selectedJob.estimatedDuration}h est.</span>
                </div>
              </div>

              {/* Contact Info */}
              {selectedJob.customerPhone && (
                <a
                  href={`tel:${selectedJob.customerPhone}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: '#e3f2fd',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    color: '#1976d2'
                  }}
                >
                  <Phone size={20} />
                  <span style={{ fontWeight: '600' }}>{selectedJob.customerPhone}</span>
                </a>
              )}

              {/* Address */}
              {selectedJob.customerAddress && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(selectedJob.customerAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: '#e8f5e9',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    color: '#388e3c'
                  }}
                >
                  <Navigation size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontWeight: '500' }}>{selectedJob.customerAddress}</span>
                </a>
              )}

              {/* Job Type & Value */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1rem',
                background: '#fff3e0',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Job Type</div>
                  <div style={{ fontWeight: '600', color: '#1a1a1a', textTransform: 'capitalize' }}>
                    {selectedJob.jobType?.replace('_', ' ')}
                  </div>
                </div>
                {selectedJob.quotedValue > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Quoted Value</div>
                    <div style={{ fontWeight: '700', color: '#388e3c', fontSize: '1.2rem' }}>
                      ${selectedJob.quotedValue?.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedJob.notes && (
                <div style={{
                  padding: '1rem',
                  background: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.5rem' }}>Notes</div>
                  <div style={{ color: '#1a1a1a', whiteSpace: 'pre-wrap' }}>{selectedJob.notes}</div>
                </div>
              )}

              {/* Job Photos */}
              <div style={{
                padding: '1rem',
                background: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <JobPhotos
                  jobId={selectedJob.id}
                  companyId={companyId}
                />
              </div>

              {/* Workflow Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e0e0e0',
                flexWrap: 'wrap'
              }}>
                {selectedJob.status === 'scheduled' && (
                  <>
                    <button
                      onClick={() => handleStartJob(selectedJob)}
                      disabled={saving}
                      style={{
                        flex: 1,
                        minWidth: '140px',
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #4ecca3 0%, #45b393 100%)',
                        color: '#000',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        opacity: saving ? 0.6 : 1
                      }}
                    >
                      <Play size={18} fill="#000" />
                      {selectedJob.job_type === 'quote_visit' ? 'Begin Visit' : 'Start Job'}
                    </button>
                  </>
                )}
                {selectedJob.status === 'in-progress' && selectedJob.job_type === 'quote_visit' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                    <button
                      onClick={() => { setSelectedJob(null); onNavigate('quote-builder', { customerId: selectedJob.customerId || selectedJob.customer_id, jobId: selectedJob.id }); }}
                      disabled={saving}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.accentGradient,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FileText size={18} />
                      Build Quote
                    </button>
                    <button
                      onClick={() => { setSelectedJob(null); onNavigate('job-planner', { jobId: selectedJob.id }); }}
                      disabled={saving}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#3498db',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Package size={18} />
                      Plan Job
                    </button>
                    <button
                      onClick={() => handleCompleteJob(selectedJob)}
                      disabled={saving}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #4ecca3 0%, #45b393 100%)',
                        color: '#000',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        opacity: saving ? 0.6 : 1
                      }}
                    >
                      <CheckCircle size={18} />
                      Complete Visit
                    </button>
                  </div>
                )}
                {selectedJob.status === 'in-progress' && selectedJob.job_type !== 'quote_visit' && (
                  <button
                    onClick={() => handleCompleteJob(selectedJob)}
                    disabled={saving}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '0.75rem',
                      background: 'linear-gradient(135deg, #4ecca3 0%, #45b393 100%)',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      opacity: saving ? 0.6 : 1
                    }}
                  >
                    <CheckCircle size={18} />
                    Complete & Invoice
                  </button>
                )}
                {selectedJob.status === 'completed' && (
                  <button
                    onClick={() => viewJobInvoice(selectedJob)}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '0.75rem',
                      background: '#3498db',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <FileText size={18} />
                    View Invoice
                  </button>
                )}
              </div>

              {/* Edit/Cancel Buttons */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginTop: '0.75rem'
              }}>
                <button
                  onClick={() => {
                    setEditingJob({
                      id: selectedJob.id,
                      status: selectedJob.status,
                      job_type: selectedJob.jobType,
                      quoted_value: selectedJob.quotedValue,
                      notes: selectedJob.notes
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Edit2 size={18} />
                  Edit Job
                </button>
                <button
                  onClick={() => handleCancelJob(selectedJob)}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#fff',
                    color: '#dc2626',
                    border: '2px solid #dc2626',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  <Trash2 size={18} />
                  Cancel Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '1rem'
          }}
          onClick={() => setEditingJob(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '450px',
              padding: '1.5rem'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#1a1a1a', fontSize: '1.25rem' }}>Edit Job</h3>
              <button
                onClick={() => setEditingJob(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} color="#666" />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#1a1a1a', fontWeight: '600' }}>Status</label>
                <select
                  value={editingJob.status || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: '#1a1a1a',
                    fontSize: '1rem'
                  }}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#1a1a1a', fontWeight: '600' }}>Job Type</label>
                <input
                  type="text"
                  value={editingJob.job_type || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, job_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: '#1a1a1a',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#1a1a1a', fontWeight: '600' }}>Quoted Value</label>
                <input
                  type="number"
                  value={editingJob.quoted_value || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, quoted_value: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: '#1a1a1a',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#1a1a1a', fontWeight: '600' }}>Notes</label>
                <textarea
                  value={editingJob.notes || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: '#1a1a1a',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button
                  onClick={() => setEditingJob(null)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateJob(editingJob.id, {
                    status: editingJob.status,
                    job_type: editingJob.job_type,
                    quoted_value: editingJob.quoted_value,
                    notes: editingJob.notes
                  })}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Job from Quote Modal */}
      {showCreateJobModal && quoteToSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1e1e2e',
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            width: '100%',
            maxWidth: '450px',
            border: '1px solid #333'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.25rem' }}>
                Schedule Job from Quote
              </h3>
              <button
                onClick={() => {
                  setShowCreateJobModal(false);
                  setQuoteToSchedule(null);
                }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Quote Info */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: '#2a2a3a',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#fff' }}>{quoteToSchedule.quote_number}</div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                {quoteToSchedule.customers?.name || prefillCustomer?.name || 'Customer'}
              </div>
              <div style={{ color: '#4ecca3', fontWeight: 'bold', marginTop: '0.5rem' }}>
                ${(quoteToSchedule.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>

            {/* Date Selection */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Date *
              </label>
              <input
                type="date"
                value={newJobDate}
                onChange={(e) => setNewJobDate(e.target.value)}
                min={getLocalDateString(new Date())}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Time Selection */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Time
              </label>
              <select
                value={newJobTime}
                onChange={(e) => setNewJobTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                {Array.from({ length: 49 }, (_, i) => {
                  const hour = Math.floor(i / 4) + 7;
                  const minute = (i % 4) * 15;
                  if (hour > 19) return null;
                  const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                  const displayStr = `${hour > 12 ? hour - 12 : hour}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
                  return <option key={timeStr} value={timeStr}>{displayStr}</option>;
                })}
              </select>
            </div>

            {/* Tech Assignment */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Assign To
              </label>
              <select
                value={newJobTech}
                onChange={(e) => setNewJobTech(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="">Unassigned</option>
                {technicians.map(tech => (
                  <option key={tech.id} value={tech.id}>{tech.name}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowCreateJobModal(false);
                  setQuoteToSchedule(null);
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: colors.textMuted,
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateJobFromQuote}
                disabled={!newJobDate || creatingJob}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: newJobDate ? '#4ecca3' : '#444',
                  border: 'none',
                  borderRadius: '8px',
                  color: newJobDate ? '#000' : colors.textMuted,
                  fontWeight: '600',
                  cursor: newJobDate && !creatingJob ? 'pointer' : 'not-allowed'
                }}
              >
                {creatingJob ? 'Creating...' : 'Create Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Completion Modal */}
      {showCompletionModal && selectedJobForCompletion && (
        <JobCompletionModal
          job={selectedJobForCompletion}
          companyId={companyId}
          onClose={() => {
            setShowCompletionModal(false);
            setSelectedJobForCompletion(null);
          }}
          onComplete={handleJobCompleted}
          onNavigate={onNavigate}
        />
      )}

      {/* Quote Visit Completion Modal */}
      {showQuoteVisitModal && selectedQuoteVisitJob && (
        <QuoteVisitCompletionModal
          job={selectedQuoteVisitJob}
          companyId={companyId}
          onClose={() => {
            setShowQuoteVisitModal(false);
            setSelectedQuoteVisitJob(null);
          }}
          onComplete={() => {
            setShowQuoteVisitModal(false);
            setSelectedQuoteVisitJob(null);
            loadJobs();
          }}
          onNavigate={onNavigate}
        />
      )}

      {/* Schedule Event Modal */}
      {showEventModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1e1e2e',
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            width: '100%',
            maxWidth: '450px',
            border: '1px solid #333'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.25rem' }}>
                Schedule Event
              </h3>
              <button
                onClick={() => setShowEventModal(false)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Event Type */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Event Type
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { value: 'meeting', label: 'Meeting', color: '#9b59b6' },
                  { value: 'training', label: 'Training', color: '#3498db' },
                  { value: 'personal', label: 'Personal', color: '#e67e22' },
                  { value: 'other', label: 'Other', color: '#95a5a6' }
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => setNewEvent(prev => ({ ...prev, event_type: type.value, color: type.color }))}
                    style={{
                      padding: '0.5rem 1rem',
                      background: newEvent.event_type === type.value ? type.color : '#2a2a3a',
                      border: `2px solid ${newEvent.event_type === type.value ? type.color : '#444'}`,
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Title *
              </label>
              <input
                type="text"
                value={newEvent.title}
                onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Team Meeting, Training Session"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Date */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Date *
              </label>
              <input
                type="date"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent(prev => ({ ...prev, event_date: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Time & Duration Row */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                  Time *
                </label>
                <select
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, start_time: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#2a2a3a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                >
                  {Array.from({ length: 49 }, (_, i) => {
                    const hour = Math.floor(i / 4) + 7;
                    const minute = (i % 4) * 15;
                    if (hour > 19) return null;
                    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                    const displayStr = `${hour > 12 ? hour - 12 : hour}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
                    return <option key={timeStr} value={timeStr}>{displayStr}</option>;
                  })}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                  Duration
                </label>
                <select
                  value={newEvent.duration_minutes}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#2a2a3a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                  <option value={180}>3 hours</option>
                  <option value={240}>4 hours</option>
                  <option value={480}>Full Day</option>
                </select>
              </div>
            </div>

            {/* Assign To */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Assign To
              </label>
              <select
                value={newEvent.assigned_to}
                onChange={(e) => setNewEvent(prev => ({ ...prev, assigned_to: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="">Everyone / Unassigned</option>
                {technicians.map(tech => (
                  <option key={tech.id} value={tech.id}>{tech.name}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fff', fontWeight: '600' }}>
                Notes (optional)
              </label>
              <textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add any notes or details..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowEventModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#2a2a3a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: colors.textMuted,
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEvent}
                disabled={!newEvent.title || !newEvent.event_date || savingEvent}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: (newEvent.title && newEvent.event_date) ? '#9b59b6' : '#444',
                  border: 'none',
                  borderRadius: '8px',
                  color: (newEvent.title && newEvent.event_date) ? '#fff' : colors.textMuted,
                  fontWeight: '600',
                  cursor: (newEvent.title && newEvent.event_date && !savingEvent) ? 'pointer' : 'not-allowed'
                }}
              >
                {savingEvent ? 'Saving...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}